import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { MessageCircle } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { useStore } from "@/context/StoreContext";
import { api, waLink } from "@/lib/api";
import { Mark } from "@/components/Logo";

export function OrderView({ order, children }) {
  const { t, tr, fmt } = useLang();
  const { settings } = useStore();
  const isWa = order.payment_method === "whatsapp";
  const msg = [
    `${t("wa_hello")} (${order.order_number})`,
    ...order.items.map((i) => `• ${tr(i.name)}${i.volume ? ` (${i.volume})` : ""} × ${i.qty} — ${fmt(i.price * i.qty)}`),
    `${t("delivery")} (${order.city_name}): ${fmt(order.delivery_fee)}`,
    `${t("total")}: ${fmt(order.total)}`,
    `${order.customer.full_name} — ${order.customer.phone}`,
    order.customer.address,
  ].join("\n");

  return (
    <div className="mx-auto max-w-2xl" data-testid="order-view">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1 }} className="flex flex-col items-center text-center">
        <Mark className="h-16 w-14 text-[#C6A76A]" draw />
        <p className="eyebrow mt-8">{t("thank_you")}</p>
        <h1 className="font-display mt-4 text-4xl sm:text-5xl">{t("order_received")}</h1>
        {children}
      </motion.div>
      {isWa && settings?.whatsapp_number && (
        <a data-testid="order-whatsapp-send-btn" href={waLink(settings.whatsapp_number, msg)} target="_blank" rel="noreferrer" className="mt-10 flex items-center justify-center gap-3 bg-[#C6A76A] py-4 text-[0.78rem] tracking-[0.22em] text-[#0B0B0C] transition-colors hover:bg-[#E3CA95] rtl:text-base rtl:tracking-normal">
          <MessageCircle className="h-4 w-4" strokeWidth={1.4} /> {t("open_wa")}
        </a>
      )}
      <div className="mt-12 border border-[#F7F3EB]/15 p-7 sm:p-10">
        <div className="grid grid-cols-2 gap-6 text-sm">
          <div><p className="text-[#F7F3EB]/60">{t("order_no")}</p><p className="mt-1 font-latin text-lg text-[#C6A76A]" data-testid="order-number">{order.order_number}</p></div>
          <div><p className="text-[#F7F3EB]/60">{t("status")}</p><p className="mt-1" data-testid="order-status">{t(`os_${order.status}`)}</p></div>
          <div><p className="text-[#F7F3EB]/60">{t("pay_status")}</p><p className="mt-1" data-testid="order-payment-status">{t(`ps_${order.payment_status}`)}</p></div>
          <div><p className="text-[#F7F3EB]/60">{t("city")}</p><p className="mt-1">{order.city_name}</p></div>
        </div>
        <div className="mt-8 divide-y divide-[#F7F3EB]/10 border-y border-[#F7F3EB]/10">
          {order.items.map((i) => (
            <div key={i.product_id} className="flex justify-between py-4 text-sm">
              <span>{tr(i.name)} {i.volume && <span className="text-[#F7F3EB]/60">· {i.volume}</span>} × {i.qty}</span>
              <span className="font-latin rtl:font-sans">{fmt(i.price * i.qty)}</span>
            </div>
          ))}
        </div>
        <div className="mt-6 space-y-2 text-sm">
          <div className="flex justify-between"><span className="text-[#F7F3EB]/70">{t("subtotal")}</span><span className="font-latin rtl:font-sans">{fmt(order.subtotal)}</span></div>
          <div className="flex justify-between"><span className="text-[#F7F3EB]/70">{t("delivery")}</span><span className="font-latin rtl:font-sans">{order.delivery_fee > 0 ? fmt(order.delivery_fee) : t("free")}</span></div>
          <div className="flex justify-between pt-3 font-display text-2xl"><span>{t("total")}</span><span className="text-[#C6A76A]" data-testid="order-total">{fmt(order.total)}</span></div>
        </div>
      </div>
      <p className="mt-8 text-center text-sm text-[#F7F3EB]/70">{t("we_contact")}</p>
      <div className="mt-8 text-center">
        <Link to="/" data-testid="order-back-home" className="link-line pb-1 text-xs tracking-[0.22em] rtl:text-sm rtl:tracking-normal">{t("back_home")}</Link>
      </div>
    </div>
  );
}

export default function OrderPage() {
  const { token } = useParams();
  const { t } = useLang();
  const [order, setOrder] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get(`/orders/${token}`).then((r) => setOrder(r.data)).catch(() => setErr(true));
  }, [token]);

  return (
    <main className="grain relative min-h-screen bg-[#0B0B0C] px-5 pb-24 pt-36 text-[#F7F3EB]" data-testid="order-page">
      {err ? <p className="text-center font-display text-3xl">{t("not_found")}</p> : order ? <OrderView order={order} /> : <p className="text-center text-sm text-[#F7F3EB]/60">{t("loading")}</p>}
    </main>
  );
}
